import React, { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Input,
  Row,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd'
import {
  ApartmentOutlined,
  BarChartOutlined,
  BoxPlotOutlined,
  FileSearchOutlined,
  InboxOutlined,
  SearchOutlined,
  ShoppingOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface TraceBale {
  bale_no: string
  grn_no: string
  lot_no?: string
  supplier_name: string
  weight_kg: string
  mic?: number
  uhml?: number
  strength?: number
  moisture_pct?: number
  godown?: string
}

interface SupplierSummary {
  supplier: string
  bale_count: number
  weight_kg: string
  mic_avg?: number
}

interface TraceResult {
  fg_lot: {
    fg_lot_no: string
    count_ne: number
    net_weight_kg: string
    production_date: string
  }
  laydown?: {
    laydown_no: string
    plan_date: string
    avg_mic: number
    avg_uhml: number
  }
  issue_slip?: {
    issue_slip_no: string
    issue_date: string
    total_bales: number
  }
  bales: TraceBale[]
  supplier_summary: SupplierSummary[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtKg(v: string | number | null | undefined) {
  if (v === null || v === undefined) return '—'
  return Number(v).toLocaleString('en-IN', { minimumFractionDigits: 3 })
}

function fmtNum(v: number | null | undefined, dp = 2) {
  if (v === null || v === undefined) return '—'
  return Number(v).toFixed(dp)
}

// ── Trace Result Display ──────────────────────────────────────────────────────

function TraceResultView({ result }: { result: TraceResult }) {
  const { fg_lot, laydown, issue_slip, bales, supplier_summary } = result

  // Build Steps — mark steps that exist as completed
  const stepItems = [
    {
      title: 'FG Lot',
      description: fg_lot.fg_lot_no,
      icon: <BoxPlotOutlined />,
      status: 'finish' as const,
    },
    {
      title: 'Issue Slip',
      description: issue_slip?.issue_slip_no ?? 'N/A',
      icon: <FileSearchOutlined />,
      status: (issue_slip ? 'finish' : 'wait') as 'finish' | 'wait',
    },
    {
      title: 'Laydown',
      description: laydown?.laydown_no ?? 'N/A',
      icon: <ApartmentOutlined />,
      status: (laydown ? 'finish' : 'wait') as 'finish' | 'wait',
    },
    {
      title: 'Bales',
      description: `${bales.length} bales`,
      icon: <InboxOutlined />,
      status: (bales.length > 0 ? 'finish' : 'wait') as 'finish' | 'wait',
    },
    {
      title: 'Suppliers',
      description: `${supplier_summary.length} supplier(s)`,
      icon: <TeamOutlined />,
      status: (supplier_summary.length > 0 ? 'finish' : 'wait') as 'finish' | 'wait',
    },
  ]

  const baleCols = [
    {
      title: 'Bale No',
      dataIndex: 'bale_no',
      key: 'bn',
      width: 130,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'GRN',
      dataIndex: 'grn_no',
      key: 'grn',
      width: 130,
      render: (v: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Lot No',
      dataIndex: 'lot_no',
      key: 'lot',
      width: 100,
      render: (v?: string) => v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'sup',
      ellipsis: true,
    },
    {
      title: 'Weight (kg)',
      dataIndex: 'weight_kg',
      key: 'wt',
      width: 100,
      align: 'right' as const,
      render: (v: string) => Number(v).toFixed(3),
    },
    {
      title: 'MIC',
      dataIndex: 'mic',
      key: 'mic',
      width: 70,
      align: 'right' as const,
      render: (v?: number) => fmtNum(v),
    },
    {
      title: 'UHML',
      dataIndex: 'uhml',
      key: 'uhml',
      width: 80,
      align: 'right' as const,
      render: (v?: number) => fmtNum(v),
    },
    {
      title: 'Moisture %',
      dataIndex: 'moisture_pct',
      key: 'moist',
      width: 90,
      align: 'right' as const,
      render: (v?: number) => fmtNum(v),
    },
    {
      title: 'Godown',
      dataIndex: 'godown',
      key: 'gd',
      width: 100,
      render: (v?: string) => v || '—',
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Timeline Steps */}
      <Card size="small">
        <Steps
          current={stepItems.filter(s => s.status === 'finish').length - 1}
          items={stepItems}
          size="small"
          style={{ padding: '8px 0' }}
        />
      </Card>

      {/* FG Lot + Laydown + Issue Slip cards */}
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card
            size="small"
            title={
              <Space>
                <BoxPlotOutlined style={{ color: '#1677ff' }} />
                FG Lot
              </Space>
            }
            style={{ borderTop: '3px solid #1677ff' }}
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Lot No">
                <Text strong style={{ fontFamily: 'monospace' }}>{fg_lot.fg_lot_no}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Count (Ne)">
                <Tag color="blue">{fg_lot.count_ne}s</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Net Weight">
                <Text strong>{fmtKg(fg_lot.net_weight_kg)} kg</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Production Date">
                {dayjs(fg_lot.production_date).format('DD-MMM-YYYY')}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            size="small"
            title={
              <Space>
                <ApartmentOutlined style={{ color: '#722ed1' }} />
                Laydown
              </Space>
            }
            style={{ borderTop: `3px solid ${laydown ? '#722ed1' : '#d9d9d9'}` }}
          >
            {laydown ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Laydown No">
                  <Text strong style={{ fontFamily: 'monospace' }}>{laydown.laydown_no}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Plan Date">
                  {dayjs(laydown.plan_date).format('DD-MMM-YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Avg MIC">
                  <Text strong>{fmtNum(laydown.avg_mic)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Avg UHML">
                  <Text strong>{fmtNum(laydown.avg_uhml)}</Text>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Laydown data not linked</Text>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            size="small"
            title={
              <Space>
                <FileSearchOutlined style={{ color: '#fa8c16' }} />
                Issue Slip
              </Space>
            }
            style={{ borderTop: `3px solid ${issue_slip ? '#fa8c16' : '#d9d9d9'}` }}
          >
            {issue_slip ? (
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Slip No">
                  <Text strong style={{ fontFamily: 'monospace' }}>{issue_slip.issue_slip_no}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Issue Date">
                  {dayjs(issue_slip.issue_date).format('DD-MMM-YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Total Bales">
                  <Tag color="orange">{issue_slip.total_bales}</Tag>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Issue slip not linked</Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* Bales Table */}
      <Card
        size="small"
        title={
          <Space>
            <InboxOutlined style={{ color: '#52c41a' }} />
            Bales ({bales.length})
          </Space>
        }
      >
        <Table
          dataSource={bales}
          columns={baleCols}
          rowKey="bale_no"
          size="small"
          pagination={bales.length > 20 ? { pageSize: 20, showTotal: t => `${t} bales` } : false}
          scroll={{ x: 900 }}
          summary={() => {
            if (bales.length === 0) return null
            const totalWt = bales.reduce((s, b) => s + Number(b.weight_kg), 0)
            return (
              <Table.Summary.Row style={{ background: '#f6ffed' }}>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong style={{ fontSize: 12 }}>Total ({bales.length} bales)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {totalWt.toFixed(3)}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={4} />
              </Table.Summary.Row>
            )
          }}
        />
      </Card>

      {/* Supplier Summary */}
      <div>
        <Text strong style={{ fontSize: 14, marginBottom: 12, display: 'block' }}>
          <TeamOutlined style={{ marginRight: 6, color: '#1677ff' }} />
          Supplier Summary ({supplier_summary.length})
        </Text>
        <Row gutter={[12, 12]}>
          {supplier_summary.map(s => (
            <Col xs={24} sm={12} md={8} lg={6} key={s.supplier}>
              <Card
                size="small"
                style={{ borderLeft: '3px solid #1677ff' }}
              >
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  <Text strong style={{ fontSize: 13 }}>{s.supplier}</Text>
                  <Row justify="space-between">
                    <Space size={4}>
                      <Tag color="blue">{s.bale_count} bales</Tag>
                    </Space>
                    <Text style={{ fontSize: 12, color: '#52c41a' }}>
                      {fmtKg(s.weight_kg)} kg
                    </Text>
                  </Row>
                  {s.mic_avg !== null && s.mic_avg !== undefined && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Avg MIC: <Text strong style={{ fontSize: 11 }}>{fmtNum(s.mic_avg)}</Text>
                    </Text>
                  )}
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </Space>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function YarnLotTracePage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [searchInput, setSearchInput] = useState('')
  const [lotNo, setLotNo] = useState('')

  const {
    data: traceResult,
    isLoading,
    isError,
    error,
    isFetched,
  } = useQuery<TraceResult>({
    queryKey: ['yarn-lot-trace', lotNo, plantId],
    queryFn: () =>
      apiClient
        .get(`/traceability/yarn-lot/${encodeURIComponent(lotNo)}`, { params: { plant_id: plantId } })
        .then(r => r.data),
    enabled: !!lotNo && !!plantId,
    retry: false,
  })

  const isNotFound =
    isError && (error as any)?.response?.status === 404

  const handleSearch = () => {
    const trimmed = searchInput.trim()
    if (!trimmed) return
    setLotNo(trimmed)
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ShoppingOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Yarn Lot Traceability
      </Title>
      <Text type="secondary">
        Full backward trace — enter a Finished Goods Lot number to see the complete chain back to supplier bales
      </Text>

      {/* Search Bar */}
      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={16} align="middle" wrap>
          <Col xs={24} sm={6} md={4}>
            <Text strong style={{ fontSize: 13 }}>Plant:</Text>
            <br />
            <select
              value={plantId}
              onChange={e => setPlantId(e.target.value)}
              style={{ width: '100%', marginTop: 4, padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9' }}
            >
              {plants.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name ?? p.plant_name}</option>
              ))}
            </select>
          </Col>
          <Col xs={24} sm={18} md={20}>
            <Text strong style={{ fontSize: 13 }}>FG Lot No:</Text>
            <br />
            <Space.Compact style={{ width: '100%', marginTop: 4 }}>
              <Input
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onPressEnter={handleSearch}
                placeholder="Enter FG Lot No (e.g. FGL-2024-001)"
                size="large"
                prefix={<SearchOutlined style={{ color: '#aaa' }} />}
                allowClear
                style={{ maxWidth: 500 }}
              />
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={isLoading}
              >
                Trace
              </Button>
            </Space.Compact>
          </Col>
        </Row>
      </Card>

      {/* Loading */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
          <br />
          <Text type="secondary" style={{ marginTop: 12, display: 'block' }}>
            Tracing lot chain…
          </Text>
        </div>
      )}

      {/* Not Found */}
      {isNotFound && (
        <Alert
          type="warning"
          showIcon
          message={`Lot Not Found: "${lotNo}"`}
          description="No FG lot found with this number for the selected plant. Check the lot number and try again."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Other Error */}
      {isError && !isNotFound && (
        <Alert
          type="error"
          showIcon
          message="Error fetching trace data"
          description={(error as any)?.response?.data?.detail ?? String(error)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Empty State — no search yet */}
      {!lotNo && !isLoading && (
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <BarChartOutlined style={{ fontSize: 48, color: '#d9d9d9', display: 'block', marginBottom: 12 }} />
          <Title level={4} type="secondary">Enter a FG Lot Number to Begin</Title>
          <Text type="secondary">
            The trace will show the complete chain: FG Lot → Issue Slip → Laydown → Bales → Suppliers
          </Text>
          <Divider />
          <Row justify="center" gutter={[24, 8]}>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <BoxPlotOutlined /> FG Lot details
              </Text>
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <ApartmentOutlined /> Laydown plan
              </Text>
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <FileSearchOutlined /> Issue slip
              </Text>
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <InboxOutlined /> Individual bales
              </Text>
            </Col>
            <Col>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <TeamOutlined /> Supplier summary
              </Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Results */}
      {traceResult && !isLoading && (
        <>
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              <Text>
                Trace complete for <Text strong style={{ fontFamily: 'monospace' }}>{traceResult.fg_lot.fg_lot_no}</Text>
                {' — '}
                <Text strong>{traceResult.bales.length} bales</Text> from{' '}
                <Text strong>{traceResult.supplier_summary.length} supplier(s)</Text>
              </Text>
            }
          />
          <TraceResultView result={traceResult} />
        </>
      )}
    </div>
  )
}
